import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { PageShell } from './layout';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { GettonciniIcon } from '../icons';
import Testimonials from './Testimonials';
import FaIcon from './FaIcon';
import { getAccountsCount } from '../api/stats';
import styles from '../styles/AccountLandingPage.module.css';
import { trackAuthCtaClicked } from '../analytics';

// Benefits of having an account
const BENEFITS = [
  {
    icon: 'fa-infinity',
    emoji: '♾️',
    title: 'Gioca senza limiti',
    description: 'Accesso completo a tutti i giochi, senza restrizioni. Gioca quanto vuoi, quando vuoi.',
    gradient: 'linear-gradient(145deg, #3b82f6, #2563eb)'
  },
  {
    icon: 'fa-rocket',
    emoji: '🚀',
    title: 'Accesso anticipato',
    description: 'Prova i nuovi giochi in anteprima.',
    gradient: 'linear-gradient(145deg, #8b5cf6, #7c3aed)'
  },
  {
    icon: 'fa-coins',
    emoji: '💎',
    title: 'Guadagna gettoncini',
    description: 'Accumula gettoncini completando partite e usali per sbloccare funzioni premium.',
    gradient: 'linear-gradient(145deg, #10b981, #059669)'
  },
  {
    icon: 'fa-fire',
    emoji: '🔥',
    title: 'Serie oltre 30 giorni',
    description: 'Mantieni traccia della tua serie e sblocca bonus speciali.',
    gradient: 'linear-gradient(145deg, #ef4444, #dc2626)'
  },
  {
    icon: 'fa-mobile-screen',
    emoji: '📱',
    title: 'Sincronizzazione multi-dispositivo',
    description: 'I tuoi progressi ti seguono ovunque. Inizia sul telefono, continua sul computer.',
    gradient: 'linear-gradient(145deg, #f59e0b, #d97706)'
  },
  {
    icon: 'fa-chart-line',
    emoji: '📊',
    title: 'Statistiche personali',
    description: 'Monitora le tue performance, i record personali e i progressi nel tempo.',
    gradient: 'linear-gradient(145deg, #06b6d4, #0891b2)'
  }
];

// Comparison table data
const COMPARISON = [
  { feature: 'Gratuito', anonymous: true, account: true },
  { feature: 'Partite ogni 7 giorni', anonymous: '5', account: 'Illimitate' },
  { feature: 'Periodo senza account', anonymous: '30 giorni dalla prima partita', account: 'Illimitato' },
  { feature: 'Accesso all’archivio', anonymous: true, account: true },
  { feature: 'Accesso a tutti i giochi', anonymous: true, account: true },
  { feature: 'Guadagna gettoncini', anonymous: false, account: true },
  { feature: 'Usa gli aiutini', anonymous: false, account: true },
  { feature: 'Serie illimitata', anonymous: false, account: true },
  { feature: 'Progressi sincronizzati', anonymous: false, account: true },
  { feature: 'Statistiche complete', anonymous: false, account: true },
];

// FAQs
const FAQS = [
  {
    question: 'È davvero gratuito?',
    answer: 'Sì, creare un account è completamente gratuito. Non ci sono costi nascosti o abbonamenti.'
  },
  {
    question: 'Quali dati vengono raccolti?',
    answer: "Solo l\'email per l\'accesso e i dati di gioco (partite completate, punteggi). Non vendiamo i tuoi dati e puoi eliminare l'account in qualsiasi momento."
  },
  {
    question: 'Posso continuare a giocare senza account?',
    answer: 'Certo! Puoi continuare a giocare in modalità anonima, ma alcune funzionalità come i gettoncini e la sincronizzazione non sono disponibili.'
  },
  {
    question: 'Come faccio a sincronizzare i miei risultati?',
    answer: 'Quando crei un account, i tuoi risultati vengono automaticamente associati al tuo profilo, independentemente dal dispositivo che usi.'
  }
];

const AccountLandingPage = () => {
  const { isSignedIn } = useUser();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const location = useLocation();
  const [accountsCount, setAccountsCount] = useState(null);

  // Fetch registered accounts count once on mount
  useEffect(() => {
    (async () => {
      const count = await getAccountsCount();
      if (typeof count === 'number' && !Number.isNaN(count)) {
        setAccountsCount(count);
      }
    })();
  }, []);

  // If already signed in, show different content
  if (isSignedIn) {
    return (
      <PageShell>
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.iconShowcase}>
              <div className={styles.checkmarkIcon}>
                <FaIcon className="fas fa-circle-check"  />
              </div>
            </div>
            <h1 className={styles.heroTitle}>Sei già connesso!</h1>
            <p className={styles.heroSubtitle}>
              Hai già un account attivo. Puoi gestire il tuo profilo 
              o tornare a giocare.
            </p>
            <div className={styles.signedInActions}>
              <Link to="/account" className={styles.primaryButton}>
                <FaIcon className="fas fa-user"  />
                <span>Vai al profilo</span>
              </Link>
              <Link to="/" className={styles.secondaryButton}>
                <FaIcon className="fas fa-gamepad"  />
                <span>Torna ai giochi</span>
              </Link>
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  const handleAuthCtaClick = (ctaKind) => {
    trackAuthCtaClicked({
      source_surface: 'account_landing',
      gate_reason: 'account_landing',
      cta_kind: ctaKind,
      next_path: `${location.pathname}${location.search}`,
      is_returning_device: hasEverAuthenticated,
    });
  };

  const signUpUrl = `/signup?source=account_landing&gate_reason=account_landing&next=${encodeURIComponent(`${location.pathname}${location.search}`)}`;
  const signInUrl = `/signin?source=account_landing&gate_reason=account_landing&next=${encodeURIComponent(`${location.pathname}${location.search}`)}`;

  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Floating icons background */}
        <div className={styles.floatingIcons}>
          {['🎮', '🏆', '💎', '🔥', '📱', '⭐', '🎯', '✨'].map((emoji, i) => (
            <div key={i} className={styles.floatingIcon}>
              {emoji}
            </div>
          ))}
        </div>

        {/* Sparkles */}
        <div className={styles.sparkles}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className={styles.sparkle} />
          ))}
        </div>

        <div className={styles.heroContent}>
          <div className={styles.iconShowcase}>
            <div className={styles.mainIcon}>
              <FaIcon className="fas fa-user-plus"  />
            </div>
          </div>

          <h1 className={styles.heroTitle}>Crea il tuo account</h1>
          
          <p className={styles.heroSubtitle}>
            Sblocca tutte le funzionalità dei Giochini di Parole. 
            È gratis e ci vogliono solo 30 secondi.
          </p>

          <div className={styles.heroCta}>
            <Link to={signUpUrl} className={styles.ctaButton} onClick={() => handleAuthCtaClick('sign_up')}>
              <FaIcon className="fas fa-user-plus"  />
              Registrati gratis
            </Link>
            
            {hasEverAuthenticated && (
              <p className={styles.signInHint}>
                Hai già un account?{' '}
                <Link to={signInUrl} className={styles.signInLink} onClick={() => handleAuthCtaClick('sign_in')}>
                  Accedi
                </Link>
              </p>
            )}
          </div>
        </div>

        <div className={styles.scrollIndicator}>
          <span>Scopri i vantaggi</span>
          <FaIcon className={`fas fa-chevron-down ${styles.scrollArrow}`}  />
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>✨</span>
            <h2 className={styles.sectionTitle}>Perché creare un account?</h2>
            <p className={styles.sectionSubtitle}>
              Ecco cosa ottieni registrandoti gratuitamente.
            </p>
          </div>

          <div className={styles.benefitsGrid}>
            {BENEFITS.map((benefit, index) => (
              <div key={index} className={styles.benefitCard}>
                <div 
                  className={styles.benefitIconWrapper}
                  style={{ background: benefit.gradient }}
                >
                  <FaIcon className={`fas ${benefit.icon}`} />
                </div>
                <div className={styles.benefitContent}>
                  <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                  <p className={styles.benefitDescription}>{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className={styles.comparisonSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>⚖️</span>
            <h2 className={styles.sectionTitle}>Anonimo vs Account</h2>
            <p className={styles.sectionSubtitle}>
              Confronta le funzionalità disponibili.
            </p>
          </div>

          <div className={styles.comparisonTableWrapper}>
            <table className={styles.comparisonTable}>
              <thead>
                <tr>
                  <th>Funzionalità</th>
                  <th>
                    <span className={styles.columnHeader}>
                      <FaIcon className="fas fa-user-secret"  />
                      Anonimo
                    </span>
                  </th>
                  <th>
                    <span className={`${styles.columnHeader} ${styles.highlighted}`}>
                      <FaIcon className="fas fa-user-check"  />
                      Con account
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, index) => (
                  <tr key={index}>
                    <td>{row.feature}</td>
                    <td>
                      {typeof row.anonymous === 'string' ? (
                        <span className={styles.limitText}>{row.anonymous}</span>
                      ) : row.anonymous ? (
                        <FaIcon className={`fas fa-check ${styles.checkIcon}`}  />
                      ) : (
                        <FaIcon className={`fas fa-xmark ${styles.xIcon}`}  />
                      )}
                    </td>
                    <td>
                      {typeof row.account === 'string' ? (
                        <span className={`${styles.limitText} ${styles.highlighted}`}>{row.account}</span>
                      ) : row.account ? (
                        <FaIcon className={`fas fa-check ${styles.checkIcon} ${styles.highlighted}`}  />
                      ) : (
                        <FaIcon className={`fas fa-xmark ${styles.xIcon}`}  />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className={styles.socialProofSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.socialProofContent}>
            <div className={styles.socialProofStat}>
              <span className={styles.statNumber}>
                {accountsCount !== null 
                  ? new Intl.NumberFormat('it-IT').format(accountsCount)
                  : 'Migliaia'}
              </span>
              <span className={styles.statLabel}>
                {accountsCount !== null ? 'giocatori già registrati' : 'di giocatori già registrati'}
              </span>
            </div>
            <p className={styles.socialProofText}>
              Unisciti alla community dei Giochini di Parole. Ogni giorno nuovi 
              giocatori scoprono quanto è meglio giocare con un account.
            </p>
            <Link to={signUpUrl} className={styles.socialProofCta} onClick={() => handleAuthCtaClick('sign_up')}>
              Unisciti a noi
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Gettoncini Teaser */}
      <section className={styles.gettonciniSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.gettonciniCard}>
            <div className={styles.gettonciniIcon}>
              <GettonciniIcon size={64} />
            </div>
            <div className={styles.gettonciniContent}>
              <h3>I tuoi primi 1.000 gettoncini</h3>
              <p>
                Registrati e ricevi subito 1.000 gettoncini di benvenuto. 
                Usali per sbloccare le funzioni premium.
              </p>
              <Link to="/gettoncini" className={styles.gettonciniLink}>
                Scopri di più sui gettoncini →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Domande frequenti</h2>
          
          <div className={styles.faqGrid}>
            {FAQS.map((faq, index) => (
              <div key={index} className={styles.faqItem}>
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className={styles.bottomCta}>
        <div className={styles.bottomCtaContent}>
          <span className={styles.bottomCtaEmoji}>🎮</span>
          <h2 className={styles.bottomCtaTitle}>Pronto a iniziare?</h2>
          <p className={styles.bottomCtaText}>
            Crea il tuo account gratuito e sblocca tutte le funzionalità 
            dei Giochini di Parole.
          </p>
          
          <Link to={signUpUrl} className={styles.bottomCtaButton} onClick={() => handleAuthCtaClick('sign_up')}>
            <FaIcon className="fas fa-user-plus"  />
            <span>Registrati gratis</span>
          </Link>

          {hasEverAuthenticated && (
            <p className={styles.bottomSignInHint}>
              Hai già un account?{' '}
              <Link to={signInUrl} className={styles.bottomSignInLink} onClick={() => handleAuthCtaClick('sign_in')}>
                Accedi qui
              </Link>
            </p>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default AccountLandingPage;
